import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ---- Types ----
export interface DbToken {
  id?: number;
  token_address: string;
  name: string;
  symbol: string;
  decimals: number;
  total_supply: string;
  creator: string;
  reserve_usdt: number;
  token_balance: string;
  current_price: string;
  migrated: boolean;
  active: boolean;
  created_at: number;
  updated_at?: string;
  description?: string;
  website?: string;
}

export interface DbTransaction {
  id?: number;
  token_address: string;
  tx_hash: string;
  tx_type: 'buy' | 'sell' | 'migrate' | 'add';
  user_address: string;
  usdt_amount?: number;
  token_amount?: string;
  price?: string;
  created_at?: string;
}

// ---- Token helpers ----

/** 同步单个代币到 Supabase（upsert） */
export async function upsertToken(token: DbToken): Promise<void> {
  const { error } = await supabase
    .from('tokens')
    .upsert(token, { onConflict: 'token_address' });
  if (error) console.error('[supabase] upsertToken:', error.message);
}

/** 批量同步代币列表（不覆盖 description / website，这两个字段由 updateTokenMeta 单独维护） */
export async function upsertTokens(tokens: DbToken[]): Promise<void> {
  if (!tokens.length) return;
  // 去掉 description / website，防止链上同步时把已存的内容清空
  const rows = tokens.map(({ description: _d, website: _w, ...rest }) => rest);
  const { error } = await supabase
    .from('tokens')
    .upsert(rows, { onConflict: 'token_address' });
  if (error) console.error('[supabase] upsertTokens:', error.message);
}

/** 读取所有代币（只返回 active=true 的记录） */
export async function fetchDbTokens(): Promise<DbToken[]> {
  const { data, error } = await supabase
    .from('tokens')
    .select('*')
    .eq('active', true)
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[supabase] fetchDbTokens:', error.message);
    return [];
  }
  return data as DbToken[];
}

/** 更新代币的 description 和 website（上币后立即写入） */
export async function updateTokenMeta(
  tokenAddress: string,
  description: string,
  website: string
): Promise<void> {
  const { error } = await supabase
    .from('tokens')
    .update({ description, website })
    .eq('token_address', tokenAddress.toLowerCase());
  if (error) console.error('[supabase] updateTokenMeta:', error.message);
}

/** 记录一笔交易 */
export async function insertTransaction(tx: DbTransaction): Promise<void> {
  const { error } = await supabase.from('transactions').insert(tx);
  if (error) console.error('[supabase] insertTransaction:', error.message);
}

/** 获取平台总 USDT 储备（数据库求和） */
export async function getTotalReserve(): Promise<number> {
  const { data, error } = await supabase
    .from('tokens')
    .select('reserve_usdt')
    .eq('active', true);
  if (error || !data) return 0;
  return (data as { reserve_usdt: number }[]).reduce(
    (sum, t) => sum + Number(t.reserve_usdt),
    0
  );
}

/**
 * 获取每个代币 24h 前的最早一笔成交价格
 * 返回 { tokenAddress: price_string } 的 map
 */
export async function fetch24hPrices(): Promise<Record<string, string>> {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('transactions')
    .select('token_address, price, created_at')
    .gte('created_at', since)
    .in('tx_type', ['buy', 'sell'])
    .order('created_at', { ascending: true });
  if (error || !data) return {};
  const map: Record<string, string> = {};
  for (const row of data as { token_address: string; price: string; created_at: string }[]) {
    if (!map[row.token_address] && row.price) {
      map[row.token_address] = row.price;
    }
  }
  return map;
}

/**
 * 获取用户对每个代币的净成本（买入USDT总和 - 卖出USDT总和）
 * 返回 { tokenAddress: costUSDT } 的 map
 */
export async function fetchUserCosts(userAddress: string): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('transactions')
    .select('token_address, tx_type, usdt_amount')
    .eq('user_address', userAddress.toLowerCase())
    .in('tx_type', ['buy', 'sell']);
  if (error || !data) return {};
  const map: Record<string, number> = {};
  for (const row of data as { token_address: string; tx_type: string; usdt_amount: number }[]) {
    const addr = row.token_address;
    if (!map[addr]) map[addr] = 0;
    if (row.tx_type === 'buy')  map[addr] += Number(row.usdt_amount || 0);
    if (row.tx_type === 'sell') map[addr] -= Number(row.usdt_amount || 0);
  }
  return map;
}



